/**
 * Creates a debounced version of a function that delays its execution
 * until after a specified delay has elapsed since the last time it was invoked.
 *
 * @param {T} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @param {boolean} [immediate=false] - Whether to call the function immediately on the leading edge
 * @returns {DebouncedFunction<T>} The debounced function
 * 
 * @example
 * const debouncedSearch = debounce((query: string) => {
 *   // API call or expensive operation
 *   fetchSearchResults(query);
 * }, 300);
 * 
 * // Call multiple times, but only executes once after 300ms of inactivity
 * searchInput.addEventListener('input', (e) => {
 *   debouncedSearch(e.target.value);
 * });
 */

type AnyFunction = (...args: any[]) => any;

interface DebouncedFunction<T extends AnyFunction> {
  (...args: Parameters<T>): void;
}

export function debounce<T extends AnyFunction>(
  func: T,
  delay: number,
): DebouncedFunction<T> {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}
